import { useEffect, useState } from "react";
import { CalendarDays, Clock, MapPin, ClipboardCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import axios, { API } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";

function formatDate(d) { return d ? new Date(d).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "-"; }

export default function GuideManasik() {
  const [events, setEvents] = useState(null);
  const [attendanceEvent, setAttendanceEvent] = useState(null);
  const [pilgrims, setPilgrims] = useState([]);

  useEffect(() => {
    axios.get(`${API}/guide/manasik`).then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, []);

  const openAttendance = async (event) => {
    setAttendanceEvent(event);
    const [jamaahRes, attendanceRes] = await Promise.all([
      axios.get(`${API}/guide/jamaah`),
      axios.get(`${API}/guide/kehadiran/${event.id}`),
    ]);
    const attMap = Object.fromEntries(attendanceRes.data.map((a) => [a.user_id, a.status]));
    setPilgrims(jamaahRes.data.map((j) => ({ ...j, attendance: attMap[j.user_id] || "" })));
  };

  const setAttendance = async (userId, status) => {
    await axios.post(`${API}/guide/kehadiran`, { event_id: attendanceEvent.id, user_id: userId, status });
    setPilgrims((prev) => prev.map((p) => (p.user_id === userId ? { ...p, attendance: status } : p)));
  };

  if (events === null) return <LoadingScreen />;

  return (
    <div data-testid="guide-manasik-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Manasik &amp; Kehadiran</h1>
        <p className="text-sm text-[#64748B]">Kelola jadwal dan catat kehadiran jamaah pada setiap sesi.</p>
      </div>

      {events.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Belum ada jadwal manasik" description="Jadwal manasik yang Anda bimbing akan tampil di sini." />
      ) : (
        <div className="space-y-4">
          {events.map((e) => (
            <div key={e.id} data-testid={`guide-manasik-item-${e.id}`} className="rounded-2xl bg-white p-5 shadow-[0_2px_10px_rgba(15,107,79,0.05)]">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h3 className="font-heading text-base font-semibold text-[#1F2937]">{e.title}</h3>
                <StatusBadge status={e.status} />
              </div>
              <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-[#64748B] sm:grid-cols-3">
                <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4" /> {formatDate(e.date)}</p>
                <p className="flex items-center gap-2"><Clock className="h-4 w-4" /> {e.time}</p>
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {e.location}</p>
              </div>
              <Button variant="outline" size="sm" data-testid={`guide-manasik-attendance-button-${e.id}`} onClick={() => openAttendance(e)} className="mt-4 gap-2 rounded-full">
                <ClipboardCheck className="h-3.5 w-3.5" /> Catat Kehadiran
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!attendanceEvent} onOpenChange={(v) => !v && setAttendanceEvent(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="guide-attendance-modal">
          <DialogHeader><DialogTitle>Kehadiran: {attendanceEvent?.title}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {pilgrims.length === 0 ? <p className="text-sm text-[#64748B]">Belum ada jamaah pada program ini.</p> : pilgrims.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg bg-[#F5F9F7] px-3 py-2" data-testid={`guide-attendance-row-${p.user_id}`}>
                <span className="text-sm font-medium text-[#1F2937]">{p.name}</span>
                <Select value={p.attendance} onValueChange={(v) => setAttendance(p.user_id, v)}>
                  <SelectTrigger data-testid={`guide-attendance-select-${p.user_id}`} className="h-9 w-36"><SelectValue placeholder="Set status" /></SelectTrigger>
                  <SelectContent><SelectItem value="hadir">Hadir</SelectItem><SelectItem value="tidak_hadir">Tidak Hadir</SelectItem><SelectItem value="izin">Izin</SelectItem></SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
